import {HttpClient, HttpErrorResponse, HttpHeaders, HttpStatusCode} from '@angular/common/http';
import {Injectable} from '@angular/core';
import * as immutable from 'immutable';
import Cookies from 'js-cookie';
import {BehaviorSubject, Observable, of, Subscriber, throwError} from 'rxjs';
import {catchError, finalize, map, tap} from 'rxjs/operators';

import {BlockedPopupReporter} from '../model/blocked-popup-reporter';
import {DomainResolver} from '../model/domain-resolver';
import {SiteSettings} from '../model/site-settings';
import {Role, User, UsersAndGroups} from '../model/user';

const PM_ISSUER = 'https://sites.process-manager.dk/';

const SSO_WINDOW_WIDTH = 600;
const SSO_WINDOW_HEIGHT = 450;

export interface ServerUser {
  ID?: number;
  username?: string;
  password?: string;
  mustChangePassword?: boolean;
  accessNode?: number;
  startNode?: number;
  admin?: boolean;
  editor?: boolean;
  showLinks?: boolean;
  seeAllHistory?: boolean;
  firstName?: string;
  lastName?: string;
  initials?: string;
  email?: string;
  language?: string;
}

export interface ServerGroup {
  name: string;
  members: number[];
}

interface LoginServerReply {
  success: boolean;
  token: string;
  settings: any;
  changePassword: boolean;
}

interface LoginReply {
  siteSettings: SiteSettings;
  user: User;
  passwordIsTemporary: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  public static readonly LOGIN_ERROR_STRING = 'login.error.user-pass';
  public static readonly LOGIN_ERROR_PUBLIC_IP_STRING = 'login.error.public.ip';
  public static readonly TOKEN_NAME_POSTFIX = '_process_manager_id_token';
  public static readonly DOMAIN_DISABLED_ERROR_STRING = 'login.error.domain.disabled';
  public static readonly DOMAIN_MISSING_ERROR_STRING = 'login.error.domain.name.missing';
  public static readonly CHANGE_PASSWORD_NAME = 'process_manager_change_password_token';
  public static readonly LANGUAGE_SELECTED_POSTFIX = '_process_manager_current_language';
  public static readonly SETTINGS_NAME_POSTFIX = '_process_manager_site_settings';
  public static readonly ID_TOKEN_POSTFIX = '_process_manager_id_token';

  private loggedInSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private userSubject: BehaviorSubject<User> = new BehaviorSubject(null);
  public user$ = this.userSubject.asObservable();
  private settingsSubject: BehaviorSubject<SiteSettings> = new BehaviorSubject<SiteSettings>(null);
  private readonly jsonHeaderOptions = {
    headers: new HttpHeaders().set('Content-Type', 'application/json'),
    withCredentials: true
  };

  constructor(private http: HttpClient, private domainResolver: DomainResolver,
              private blockedPopupReporter: BlockedPopupReporter) {
    domainResolver.domainName$.subscribe(domain => this._domain = domain);
  }

  private _domain: string;

  get domain(): string {
    return this._domain?.toLowerCase() || '';
  }

  get user(): User {
    return this.userSubject.getValue();
  }

  public static getCurrentToken(domain: string) {
    const domainIdToken = domain.toLowerCase() + UserService.ID_TOKEN_POSTFIX;
    const cookieToken = Cookies.get(domainIdToken);
    if (cookieToken) {
      return cookieToken;
    }
    return localStorage.getItem(domainIdToken);
  }

  checkAndUpdateLoginState(domain: string): Observable<number> {
    domain = domain.toLowerCase();
    return this.reloadSettings(domain)
      .pipe(map(() => HttpStatusCode.Ok), tap(() => this.userSubject.next(this.decodeUser(domain))), catchError((err) => {
        this.settingsSubject.next(null);
        console.warn('checkAndUpdateLoginState caught error, user set to null', err);
        this.userSubject.next(null);
        return of(err.status || HttpStatusCode.ServiceUnavailable);
      }));
  }

  public offlineLogin = (user: User, settings: SiteSettings) => {
    this.userSubject.next(user);
    this.settingsSubject.next(settings);
  }

  private handleLoginReply = (domain: string) => (res: LoginServerReply) => {
    domain = domain.toLowerCase();
    if (!res.success) {
      localStorage.removeItem(UserService.TOKEN_NAME_POSTFIX);
      // Temporary workaround for old-style loading
      document.cookie = 'token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
      throw new Error(UserService.LOGIN_ERROR_STRING);
    }

    localStorage.setItem(domain + UserService.ID_TOKEN_POSTFIX, res.token);
    localStorage.setItem(UserService.CHANGE_PASSWORD_NAME, Boolean(res.changePassword).toString());
    this.loggedInSubject.next(res.success);

    const siteSettings: SiteSettings = this.convertSiteSettings(res.settings);
    this.settingsSubject.next(siteSettings);

    const user = this.decodeUser(domain);
    this.userSubject.next(user);

    return {
      siteSettings: siteSettings,
      user: user,
      passwordIsTemporary: res.changePassword
    };
  };

  private handleError = err => {
    switch ((err as HttpErrorResponse).status) {
      case 404:
        throw new Error(UserService.DOMAIN_MISSING_ERROR_STRING);
      case 409:
        throw new Error(UserService.DOMAIN_DISABLED_ERROR_STRING);
      case 403:
        throw new Error(UserService.LOGIN_ERROR_PUBLIC_IP_STRING);
      default:
        throw new Error(UserService.LOGIN_ERROR_STRING);
    }
  }

  private doSSORefresh(domain, subscriber?: Subscriber<void>) {
    const top = window.top.outerHeight / 2 + window.top.screenY - (SSO_WINDOW_HEIGHT / 2);
    const left = window.top.outerWidth / 2 + window.top.screenX - (SSO_WINDOW_WIDTH / 2);

    const popup = window.open(`${this.domainResolver.api}auth/${domain.toLowerCase()}?callingBase=${document.baseURI}`,
      'SSOLogin',
      `width=${SSO_WINDOW_WIDTH},height=${SSO_WINDOW_HEIGHT},left=${left},top=${top},location=on,menubar=on,status=on,resizable=on`);

    if (!popup) {
      this.blockedPopupReporter.open().subscribe(() => {
        if (!subscriber.closed) {
          this.doSSORefresh(domain, subscriber);
        }
      });
    } else {
      const handle = window.setInterval(() => {
        if (popup.closed) {
          window.clearInterval(handle);
          subscriber.next();
          subscriber.complete();
        }
      }, 100);
    }
  }

  refresh(domain = this.domain): Observable<any> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};

    let user = this.userSubject.getValue();

    if (!user) {
      try {
        user = this.decodeUser(domain, false);
      } catch (e) {
        console.warn('ID token missing or invalid', e);
      }
    }

    if (!!user) {
      if (user?.isLocalPMUser) {
        return this.http
          .post(`${this.domainResolver.api}auth/local/${domain.toLowerCase()}/session`, null, options)
          .pipe(map(this.handleLoginReply(domain)), catchError(this.handleError));
      } else {
        return new Observable(subscriber => {
          this.doSSORefresh(domain, subscriber);
        })
      }
    } else {
      return throwError('No user, cannot refresh');
    }
  }

  login(domain, username, password, userIsPublic): Observable<LoginReply> {
    const options = {headers: new HttpHeaders().set('Content-Type', 'application/json')};

    return this.http
      .post<LoginServerReply>(`${this.domainResolver.api}auth/local/${domain.toLowerCase()}/session`, JSON.stringify({
        'username': username,
        'password': password,
        'user_is_public': userIsPublic
      }), options).pipe(map(this.handleLoginReply(domain)), catchError(this.handleError))
  }

  resetRequest(domain: string, email: string, language: string): Observable<void> {
    return this.http.post<void>(
      `${this.domainResolver.api}auth/local/${domain.toLowerCase()}/passwordreset/${encodeURIComponent(email)}`, null, {
        params: {
          lang: language
        }
      });
  }

  logout(domain: string) {
    domain = domain.toLowerCase();

    this.http.post<void>(`${this.domainResolver.api}auth/local/${domain.toLowerCase()}/logout`, null).pipe(finalize(() => {
      localStorage.removeItem(domain + UserService.TOKEN_NAME_POSTFIX);
      localStorage.removeItem(domain + UserService.SETTINGS_NAME_POSTFIX);
      document.cookie = 'token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    })).subscribe();

    this.userSubject.next(null);
  }

  getSiteSettings(): SiteSettings {
    return this.settingsSubject.getValue();
  }

  getUsersAndGroups(): Observable<UsersAndGroups> {
    const user = this.user;
    if (!user) {
      return of({
        users: [],
        groups: []
      } as UsersAndGroups);
    }

    return this.http.get<UsersAndGroups>(this.domainResolver.api + this.domain + '/users/');
  }

  public getUserDetails(username: string): Observable<ServerUser> {
    return this.http.get<ServerUser>(this.domainResolver.api + this.domain + '/users/' + username).pipe(map(user => {
      return {
        ...user,
        admin: !!user.admin,
        editor: !!user.editor,
        showLinks: !!user.showLinks
      }
    }));
  }

  confirmPassword(password: any): Observable<boolean> {
    return this.http.put<void>(this.domainResolver.api + this.domain + '/security/passwordCheck', {password},
      this.jsonHeaderOptions).pipe(map(() => true), catchError(() => of(false)));
  }

  updatePassword(currentPassword: string, newPassword: string): Observable<void> {
    return this.http.put<void>(this.domainResolver.api + this.domain + '/security/password', {
      currentPassword,
      newPassword
    }, this.jsonHeaderOptions);
  }

  updatePasswordFromToken(domain: string, email: string, resetToken: string, newPassword: string): Observable<void> {
    return this.http.put<void>(`${this.domainResolver.api}auth/local/${domain.toLowerCase()}/password/bytoken`, {
      email,
      newPassword,
      resetToken
    }, this.jsonHeaderOptions);
  }

  public createUser(user: ServerUser): Observable<void> {
    return this.http.post<void>(this.domainResolver.api + this.domain + '/users/', user, this.jsonHeaderOptions);
  }

  public updateUser(user: ServerUser, oldUsername: string = user.username): Observable<void> {
    return this.http.put<void>(this.domainResolver.api + this.domain + '/users/' + oldUsername, user, this.jsonHeaderOptions);
  }

  public deleteUser(username: string): Observable<void> {
    return this.http.delete<void>(this.domainResolver.api + this.domain + '/users/' + username);
  }

  public getGroupMembers(group: string): Observable<number[]> {
    return this.http.get<number[]>(this.domainResolver.api + this.domain + '/groups/' + group);
  }

  public createGroup(group: ServerGroup): Observable<void> {
    return this.http.post<void>(this.domainResolver.api + this.domain + '/groups/', group);
  }

  public updateGroup(group: ServerGroup, oldGroupName: string = group.name): Observable<void> {
    return this.http.put<void>(this.domainResolver.api + this.domain + '/groups/' + oldGroupName, group);
  }

  public deleteGroup(groupName: string): Observable<void> {
    return this.http.delete<void>(this.domainResolver.api + this.domain + '/groups/' + groupName);
  }

  public reloadSettings(domain: string = this.domain): Observable<SiteSettings> {
    if (!domain) {
      return throwError('No domain');
    }

    return this.http.get(this.domainResolver.api + domain.toLowerCase() + '/settings', this.jsonHeaderOptions)
      .pipe(map(settings => this.convertSiteSettings(settings)), tap(settings => {
        this.settingsSubject.next(settings);
      }));
  }

  private convertSiteSettings(res): SiteSettings {
    const browserMap: immutable.Map<string, string> = immutable.Map(
      res.browsermap.map(map => [map.folder[0], map.replace[0]]));
    let roles: immutable.Map<string, string[]> = immutable.Map();
    if (!!res.roles) {
      roles = immutable.Map<string, string[]>(Object.entries(res.roles).map(([key, value]) => {
        if (!Array.isArray(value)) {
          value = [value];
        }
        return [key as string, value as string[]];
      }))
    }
    return {
      showPath: !res.hidepath,
      showSiteMap: res.sitemapenabled,
      showFavorites: res.showFavorites,
      showNavMenu: res.navmenu,
      registerVisits: res.registerVisits,
      showStatistics: res.statistics,
      showLogoutLogbook: res.showLogoutLogbook || 'none',
      useServerMail: res.servermail,
      vendorFeatures: res.vendorFeatures,
      allowedTypes: res.allowedtype,
      languages: res.language,
      browserMap: browserMap,
      rootId: res.rootId,
      roles: roles,
      styling: res.styling,
      localGroupAdministration: res.localGroupAdministration,
      localUserAdministration: res.localUserAdministration,
      subscriptionState: res.subscriptionState || 'legacy',
      licencedTemplates: res.licencedTemplates || [],
      offlineEnabled: res.offlineEnabled || false
    }
  }

  private decodeUser(domain: string, useSiteSettings: boolean = true): User {
    const decodedToken: any = this.domainResolver.resolveToken(UserService.getCurrentToken(domain.toLowerCase()));

    if (!decodedToken) {
      return null;
    }

    let roles: Role[] = [];
    let rootId: number;

    if (useSiteSettings) {
      roles = this.getRoles(decodedToken)
      rootId = this.getSiteSettings().rootId;
    }

    return {
      isLocalPMUser: decodedToken.iss === PM_ISSUER,
      userIsPublic: !!decodedToken.pm_user_public,
      username: decodedToken.pm_username || decodedToken.preferred_username || decodedToken.upn ||
        decodedToken.unique_name,
      firstName: decodedToken.pm_first_name || decodedToken.given_name,
      lastName: decodedToken.pm_last_name || decodedToken.family_name,
      email: decodedToken.pm_email || decodedToken.email,
      roles: roles,
      groups: decodedToken.pm_groups || roles,
      accessId: decodedToken.pm_access_node || rootId,
      startId: decodedToken.pm_start_node || decodedToken.pm_access_node || rootId,
      language: decodedToken.pm_user_language && parseInt(decodedToken.pm_user_language)
    };
  }

  private getRoles(decodedToken: any): Role[] {
    const siteSettings = this.getSiteSettings();

    if (siteSettings.roles.size > 0) {
      const foundRoles = decodedToken.pm_roles || decodedToken.roles || decodedToken.role ||
        decodedToken['cognito:groups'] || [];
      const hasRole = (role: string) => {
        const roleMembers = siteSettings.roles.get(role) || [];
        return roleMembers.length === 0 || roleMembers.filter(x => foundRoles.includes(x)).length > 0;
      }
      return (['admin', 'edit', 'showLinks'] as Role[]).filter(hasRole);
    } else {
      return decodedToken.pm_roles || [] as Role[];
    }
  }
}
